const STORAGE_KEY = 'theme:wishlist:v1';

function safeParseJSON(value, fallback) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function readWishlistIds() {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const parsed = safeParseJSON(raw, []);
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((id) => typeof id === 'string' && id.length);
}

function writeWishlistIds(ids) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
}

function normalizeProductId(id) {
  if (!id) return '';
  const str = String(id);
  // Support either plain numeric IDs ("123") or GID ("gid://shopify/Product/123")
  const gidMatch = str.match(/gid:\/\/shopify\/Product\/(\d+)/);
  return gidMatch ? gidMatch[1] : str;
}

function isInWishlist(productId) {
  const id = normalizeProductId(productId);
  if (!id) return false;
  return readWishlistIds().includes(id);
}

function addToWishlist(productId) {
  const id = normalizeProductId(productId);
  if (!id) return;
  const ids = readWishlistIds();
  if (ids.includes(id)) return;
  ids.unshift(id);
  writeWishlistIds(ids);
}

function removeFromWishlist(productId) {
  const id = normalizeProductId(productId);
  if (!id) return;
  const ids = readWishlistIds().filter((x) => x !== id);
  writeWishlistIds(ids);
}

function toggleWishlist(productId) {
  const id = normalizeProductId(productId);
  if (!id) return { inWishlist: false };
  const inWishlist = isInWishlist(id);
  if (inWishlist) removeFromWishlist(id);
  else addToWishlist(id);
  return { inWishlist: !inWishlist };
}

function getButtonProductId(button) {
  return (
    button?.dataset?.productId ||
    button?.getAttribute?.('data-product-id') ||
    ''
  );
}

function updateToggleButtonState(button) {
  const productId = getButtonProductId(button);
  if (!productId) return;
  const inWishlist = isInWishlist(productId);
  button.setAttribute('aria-pressed', inWishlist ? 'true' : 'false');
  button.dataset.wishlistState = inWishlist ? 'saved' : 'unsaved';

  const savedText = button.getAttribute('data-wishlist-saved-text') || 'Saved';
  const unsavedText = button.getAttribute('data-wishlist-unsaved-text') || 'Wishlist';
  const label = inWishlist ? savedText : unsavedText;

  const labelEl = button.querySelector('[data-wishlist-label]');
  if (labelEl) labelEl.textContent = label;
  else button.textContent = label;
}

function updateAllToggleButtons() {
  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    updateToggleButtonState(button);
  });
}

function ensureWishlistItemsCache() {
  const key = `${STORAGE_KEY}:items`;
  const raw = window.localStorage.getItem(key);
  const parsed = safeParseJSON(raw, {});
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { key, cache: {} };
  return { key, cache: parsed };
}

function cacheWishlistItemFromButton(button) {
  const productId = normalizeProductId(getButtonProductId(button));
  if (!productId) return;

  const { key, cache } = ensureWishlistItemsCache();

  cache[productId] = {
    id: productId,
    handle: button.dataset.productHandle || '',
    url: button.dataset.productUrl || '',
    title: button.dataset.productTitle || '',
    image: button.dataset.productImage || '',
    price: button.dataset.productPrice || '',
  };

  window.localStorage.setItem(key, JSON.stringify(cache));
}

function readWishlistItems() {
  const ids = readWishlistIds();
  const { cache } = ensureWishlistItemsCache();
  return ids.map((id) => cache[id]).filter(Boolean);
}

function renderWishlistPage() {
  const root = document.querySelector('[data-wishlist-page]');
  if (!root) return;

  const list = root.querySelector('[data-wishlist-list]');
  const empty = root.querySelector('[data-wishlist-empty]');
  if (!list || !empty) return;

  const items = readWishlistItems();
  if (!items.length) {
    list.innerHTML = '';
    empty.hidden = false;
    return;
  }

  empty.hidden = true;
  list.innerHTML = items
    .map((item) => {
      const imageHtml = item.image
        ? `<img class="wishlist__image" loading="lazy" src="${item.image}" alt="">`
        : `<div class="wishlist__image wishlist__image--placeholder" aria-hidden="true"></div>`;

      const safeTitle = (item.title || '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
      const safeUrl = (item.url || '#').replaceAll('"', '&quot;');
      const safePrice = (item.price || '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

      return `
        <li class="wishlist__item">
          <a class="wishlist__link" href="${safeUrl}">
            ${imageHtml}
            <div class="wishlist__meta">
              <div class="wishlist__title">${safeTitle}</div>
              ${safePrice ? `<div class="wishlist__price">${safePrice}</div>` : ``}
            </div>
          </a>
          <button
            type="button"
            class="wishlist__remove button button--secondary"
            data-wishlist-remove
            data-product-id="${item.id}"
            aria-label="Remove from wishlist"
          >
            Remove
          </button>
        </li>
      `;
    })
    .join('');
}

function onDocumentClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const toggleButton = target.closest('[data-wishlist-toggle]');
  if (toggleButton) {
    event.preventDefault();
    event.stopPropagation();

    const productId = getButtonProductId(toggleButton);
    if (!productId) return;

    cacheWishlistItemFromButton(toggleButton);
    toggleWishlist(productId);
    updateAllToggleButtons();
    renderWishlistPage();
    return;
  }

  const removeButton = target.closest('[data-wishlist-remove]');
  if (removeButton) {
    event.preventDefault();
    const productId = getButtonProductId(removeButton);
    if (!productId) return;
    removeFromWishlist(productId);
    updateAllToggleButtons();
    renderWishlistPage();
  }
}

function initWishlist() {
  updateAllToggleButtons();
  renderWishlistPage();

  document.addEventListener('click', onDocumentClick, { capture: true });
  window.addEventListener('storage', (e) => {
    if (!e || (e.key !== STORAGE_KEY && e.key !== `${STORAGE_KEY}:items`)) return;
    updateAllToggleButtons();
    renderWishlistPage();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlist, { once: true });
} else {
  initWishlist();
}

